import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import { getEnv } from '../config';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type LoginMode = 'google' | 'passcode';
type Step = 'username' | 'password';

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation('auth');
  const { login, loginWithGoogle } = useAuth();
  const [loginMode, setLoginMode] = useState<LoginMode>('google');
  const [currentStep, setCurrentStep] = useState<Step>('username');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const googleClientId = getEnv('VITE_GOOGLE_OAUTH_CLIENT_ID');

  const handleLoginSuccess = () => {
    setCurrentStep('username');
    setUsername('');
    setPassword('');
    setError('');
    setLoginMode('google');
    if (onSuccess) {
      onSuccess();
    } else {
      onClose();
    }
  };

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) {
      setError(t('errors.loginFailed'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const success = await loginWithGoogle(credentialResponse.credential);
      if (success) {
        handleLoginSuccess();
      } else {
        setError(t('errors.googleNoAccount'));
      }
    } catch {
      setError(t('errors.loginFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError(t('errors.googleFailed'));
  };

  const handleUsernameSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim()) {
      setError(t('errors.usernameRequired'));
      return;
    }
    setError('');
    setCurrentStep('password');
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) {
      setError(t('errors.passcodeRequired'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const success = await login(username, password);
      if (success) {
        handleLoginSuccess();
      } else {
        setError(t('errors.invalidCredentials'));
      }
    } catch {
      setError(t('errors.loginFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    setCurrentStep('username');
    setPassword('');
    setError('');
  };

  const handleClose = () => {
    setCurrentStep('username');
    setUsername('');
    setPassword('');
    setError('');
    setLoginMode('google');
    onClose();
  };

  if (!isOpen) return null;

  const steps = [
    { id: 'username', label: t('username'), completed: currentStep === 'password' },
    { id: 'password', label: t('safetyPasscode'), completed: false },
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{t('signIn')}</h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {loginMode === 'google' ? (
              <div className="space-y-4">
                {/* Google Sign-In button */}
                {googleClientId ? (
                  <div className="flex justify-center">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={handleGoogleError}
                      size="large"
                      width="350"
                      text="signin_with"
                      shape="rectangular"
                    />
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                    {t('googleNotConfigured')}
                  </p>
                )}

                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex items-center">
                      <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-sm text-red-700 font-medium">{error}</p>
                    </div>
                  </div>
                )}

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300 dark:border-gray-600" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                      {t('orDivider')}
                    </span>
                  </div>
                </div>

                {/* Safety passcode fallback link */}
                <button
                  type="button"
                  onClick={() => { setLoginMode('passcode'); setError(''); }}
                  className="w-full text-center text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                >
                  {t('usePasscodeLogin')}
                </button>
              </div>
            ) : (
              <>
                {/* Back to Google login */}
                {googleClientId && (
                  <button
                    type="button"
                    onClick={() => { setLoginMode('google'); setCurrentStep('username'); setError(''); setUsername(''); setPassword(''); }}
                    className="mb-4 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors flex items-center"
                  >
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    {t('backToGoogle')}
                  </button>
                )}

                <div className="flex">
                  {/* Vertical Stepper */}
                  <div className="flex flex-col mr-8">
                    {steps.map((step, index) => (
                      <div key={step.id} className="flex items-start">
                        <div className="flex flex-col items-center">
                          {/* Step Circle */}
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                              step.completed
                                ? 'bg-green-500 text-white'
                                : currentStep === step.id
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-200 text-gray-500 dark:text-gray-400'
                            }`}
                          >
                            {step.completed ? (
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : (
                              index + 1
                            )}
                          </div>
                          {/* Connector Line */}
                          {index < steps.length - 1 && (
                            <div
                              className={`w-0.5 h-16 ${
                                step.completed ? 'bg-green-500' : 'bg-gray-200'
                              }`}
                            />
                          )}
                        </div>
                        {/* Step Label */}
                        <div className="ml-3 mt-1">
                          <p
                            className={`text-sm font-medium ${
                              currentStep === step.id ? 'text-blue-600' : step.completed ? 'text-green-600' : 'text-gray-500 dark:text-gray-400'
                            }`}
                          >
                            {step.label}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Form Content */}
                  <div className="flex-1">
                    {currentStep === 'username' ? (
                      <form onSubmit={handleUsernameSubmit} className="space-y-4">
                        <div>
                          <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {t('enterUsername')}
                          </label>
                          <input
                            type="text"
                            id="username"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder={t('username')}
                            autoFocus
                          />
                        </div>
                        {error && (
                          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                            <div className="flex items-center">
                              <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <p className="text-sm text-red-700 font-medium">{error}</p>
                            </div>
                          </div>
                        )}
                        <button
                          type="submit"
                          className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                        >
                          {t('common:buttons.continue')}
                        </button>
                      </form>
                    ) : (
                      <form onSubmit={handlePasswordSubmit} className="space-y-4">
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                            {t('signingInAs')} <span className="font-medium text-gray-900 dark:text-white">{username}</span>
                          </p>
                          <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            {t('enterPasscode')}
                          </label>
                          <input
                            type="password"
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder={t('safetyPasscode')}
                            autoFocus
                          />
                        </div>
                        {error && (
                          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                            <div className="flex items-center">
                              <svg className="w-5 h-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <p className="text-sm text-red-700 font-medium">{error}</p>
                            </div>
                          </div>
                        )}
                        <div className="flex space-x-3">
                          <button
                            type="button"
                            onClick={handleBack}
                            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                          >
                            {t('common:buttons.back')}
                          </button>
                          <button
                            type="submit"
                            disabled={isLoading}
                            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isLoading ? (
                              <span className="flex items-center justify-center">
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                {t('signingIn')}
                              </span>
                            ) : (
                              t('signIn')
                            )}
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
